import { Injectable } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { apiCallWrapper, queryToParams } from "../api/api.util";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import { CollectionApi } from "../api/colllection.api";
import { Observable, shareReplay, map, of } from "rxjs";
import { ICollection } from "../model/collection.model";
import { AuthService } from "./auth.service";
import { LoadingService } from "./core/loading.service";
import { SessionApi } from "../api/session.api";
import { SecurityService } from "./security.service";
import { SubscriptionGroup } from "../util/subscriptionGroup";

@Injectable()
export class CollectionService {
  private collections$!: Observable<ICollection[]>;
  private readonly subscriptionGroup = new SubscriptionGroup();

  constructor(
    private notifications: NotificationsService,
    private collectionApi: CollectionApi,
    private authService: AuthService,
    private loadingService: LoadingService,
    private sessionApi: SessionApi,
    private security: SecurityService,
  ) {
    this.monitorCollections();
  }

  monitorCollections() {
    this.subscriptionGroup.add(
      this.security.isAuthenticated().subscribe({
        next: () => {
          this.initCollections();
        },
      }),
    );
  }

  private initCollections() {
    const authState = this.sessionApi.$userData.getValue();

    if (authState && !authState.isAdmin) {
      const query: IQueryFilter = new IQueryFilter({
        sortBy: "name",
        limit: 1000,
      });

      this.collections$ = this.collectionApi.list(query).pipe(
        map((res) => res.rows || []),
        shareReplay(1),
      );
    } else {
      this.collections$ = of([]);
    }
  }

  public getCollections(): Observable<ICollection[]> {
    return this.collections$;
  }

  public refreshCollections() {
    this.initCollections();
  }

  public list(query: IQueryFilter): Observable<QueryResult<ICollection>> {
    return this.collectionApi.list(query);
  }

  public delete(id: number): Observable<{ success: boolean; deleted: number }> {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.delete(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Delete Collection",
      },
    );
  }

  public get(id: number): Observable<ICollection> {
    return apiCallWrapper(this.collectionApi.get(id), {
      notificationsService: this.notifications,
      action: "Fetching Collection By Id",
    });
  }

  public update(id: number, data: ICollection) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.update(id, data),
      ),
      {
        notificationsService: this.notifications,
        action: "Update Collection",
      },
    );
  }

  public create(data: ICollection) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.create(data),
      ),
      {
        notificationsService: this.notifications,
        action: "Create Collection",
      },
    );
  }

  public getCollectionAllocationsForProduct(
    productId?: number,
    customerId?: number,
    customeruserId?: number,
  ): Observable<QueryResult<ICollection>> {
    const query: IQueryFilter = new IQueryFilter({
      sortBy: "name",
      skip: 0,
      limit: 100,
    });

    const params: Record<string, string | number | boolean> = {
      availableAllocations: true,
    };

    if (productId) params["productId"] = productId;
    if (customeruserId) params["customeruserId"] = customeruserId;
    if (customerId) params["customerId"] = customerId;

    let paramsNew = queryToParams(query);

    Object.keys(params).forEach((key) => {
      paramsNew = paramsNew.set(key, params[key]);
    });

    return this.collectionApi.collectionList(paramsNew);
  }

  public restore(id: number | string) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.restore(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Restore Collection",
      },
    );
  }

  public createCollectionProduct(data: {
    collectionId: string;
    productId: number;
  }) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.createCollectionProduct(data),
      ),
      {
        notificationsService: this.notifications,
        action: "Add Collection to Product",
      },
    );
  }

  public deleteCollectionProduct(id: number) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.collectionApi.deleteCollectionProduct(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Delete Product Collection",
      },
    );
  }
}
